/**
 * The registrar's Classroom course intake (ADR 0037, issue #1050).
 *
 * An administrator connects their own Google account with read-only scopes,
 * lists the ACTIVE Classroom courses that grant returns, previews one (which
 * stores nothing), and confirms one decision per person. A Classroom user id
 * links an IMAM person only when IMAM already verified it; name and email are
 * evidence on the screen and never a key, so the preview never suggests a
 * candidate by them.
 */
import { commitOptions } from "./commitSeams";
import { ApiError, api } from "@/lib/api/client";
import type { PeopleStudentStatus } from "@/lib/api/people";

export type ClassroomIntakeConnectionState = "none" | "ready" | "reconnect_required";

export interface ClassroomIntakeConnection {
  state: ClassroomIntakeConnectionState;
  /** Either part may be null when Google returns no profile for the grant. */
  account: { name: string | null; email: string | null } | null;
  scopes_missing: string[];
}

export interface ClassroomIntakeLinkedSection {
  course_id: string;
  code: string;
  title: string;
  term_name: string | null;
  /**
   * Whose kind of Google grant reads the link: an administrator's intake
   * connection, a teacher's teaching connection, or `lost` when that grant has
   * ended, in which case the registrar may take the reads over (contract v4 D6).
   */
  custody: ClassroomIntakeLinkCustody;
}

export type ClassroomIntakeLinkCustody = "intake" | "teaching" | "lost";

export interface ClassroomIntakeCourse {
  provider_course_id: string;
  name: string;
  section: string | null;
  state: string;
  /** Null when Classroom names no owner; the name is null when Google returns no profile. */
  owner: { provider_user_id: string; name: string | null } | null;
  alternate_link: string | null;
  creation_time: string | null;
  /** Set when an IMAM section already links this Classroom course; such a course offers no intake. */
  linked_section: ClassroomIntakeLinkedSection | null;
}

export type ClassroomIntakeVerifiedVia = "managed_identity" | "google_sign_in" | "classroom_connection";

export type ClassroomIntakeDisposition =
  | {
    kind: "verified";
    person_id: string;
    person_name: string | null;
    via: ClassroomIntakeVerifiedVia;
    /** Teachers. */
    faculty_profile_id?: string | null;
    /** Students. */
    student_id?: string | null;
    sid?: string | null;
    student_status?: PeopleStudentStatus | null;
    enrolled_in_target?: boolean;
  }
  /** Refused at apply until the people are repaired. */
  | { kind: "ambiguous"; people: ClassroomIntakeAmbiguousPerson[] }
  /** The registrar chooses a person, creates one, or skips the row. */
  | { kind: "needs_choice" };

export interface ClassroomIntakeAmbiguousPerson {
  person_id: string;
  name: string | null;
}

/** One Classroom teacher or student. Name and email are evidence only. */
export interface ClassroomIntakePersonRow {
  provider_user_id: string;
  name: { given: string | null; family: string | null; full: string | null };
  email: string | null;
  /** Teachers only. */
  is_owner?: boolean;
  disposition: ClassroomIntakeDisposition;
}

export type ClassroomIntakeTarget =
  | { mode: "new_draft" }
  | { mode: "existing_section"; course_id: string; code: string; title: string; term_name: string | null; updated_at?: string };

export interface ClassroomIntakePreview {
  course: {
    provider_course_id: string;
    name: string;
    section: string | null;
    state: string;
    alternate_link: string | null;
    owner_provider_user_id: string | null;
  };
  target: ClassroomIntakeTarget;
  /** The code is always typed by the registrar. */
  proposed_section: { title: string; code: null };
  /** The course owner when the owner is a teacher, else null. */
  proposed_primary_provider_user_id: string | null;
  teachers: ClassroomIntakePersonRow[];
  students: ClassroomIntakePersonRow[];
  counts: { teachers: number; students: number; verified: number; ambiguous: number; needs_choice: number };
  /** sha256 hex of the canonical preview payload; apply refuses a different one. */
  preview_digest: string;
}

export type ClassroomIntakeDecision = "link_verified" | "link_person" | "create" | "skip";

export interface ClassroomIntakeTeacherDecision {
  provider_user_id: string;
  decision: ClassroomIntakeDecision;
  /** `link_person`. */
  person_id?: string;
  /** `create`. */
  honorific?: string;
  /** `create`: the registrar's edit of the Classroom name; absent keeps it. */
  given_name?: string;
  family_name?: string;
}

export interface ClassroomIntakeStudentDecision {
  provider_user_id: string;
  decision: ClassroomIntakeDecision;
  /** `link_person`; the person must hold a student record. */
  person_id?: string;
  /** `create`: the program's code, the cohort's name, and the admitted year. */
  program_key?: string;
  cohort_key?: string;
  admitted_year?: number;
  /** `create`: the registrar's edit of the Classroom name; absent keeps it. */
  given_name?: string;
  family_name?: string;
}

export type ClassroomIntakeSectionChoice =
  | { mode: "new_draft"; code: string; title: string; semester_id: string }
  | { mode: "existing_section"; course_id: string; expected_updated_at?: string };

export interface ClassroomIntakeApplyRequest {
  provider_course_id: string;
  preview_digest: string;
  section: ClassroomIntakeSectionChoice;
  /** A teacher row with a resolved or created person. */
  primary_provider_user_id: string;
  teachers: ClassroomIntakeTeacherDecision[];
  students: ClassroomIntakeStudentDecision[];
  pair_learners: true;
}

export type ClassroomIntakeCreatedKey = "persons" | "faculty_profiles" | "students" | "enrollments" | "pairings";

/** One Classroom row as the apply settled it. */
export interface ClassroomIntakeAppliedPerson {
  provider_user_id: string;
  role: "teacher" | "student";
  decision: ClassroomIntakeDecision;
  person_id: string | null;
  faculty_profile_id: string | null;
  student_id: string | null;
  sid: string | null;
}

export interface ClassroomIntakeApplyResult {
  receipt_id: string;
  course_id: string;
  /** Counts of the records this apply created. */
  created: Record<ClassroomIntakeCreatedKey, number>;
  skipped: number;
  roster_batch_id: string | null;
  people: ClassroomIntakeAppliedPerson[];
}

/** A stored receipt's row, with its person's name as IMAM holds it now (display only). */
export interface ClassroomIntakeReceiptPerson extends ClassroomIntakeAppliedPerson {
  name: string | null;
}

/** A stored receipt: the apply result, who confirmed it, and when. */
export interface ClassroomIntakeReceipt extends ClassroomIntakeApplyResult {
  people: ClassroomIntakeReceiptPerson[];
  actor_name: string | null;
  created_at: string;
  provider_course_id: string;
  /** The section the apply made or linked; null when it can no longer be read. */
  course: { course_id: string; code: string; title: string; term_name: string | null } | null;
}

export interface ClassroomIntakeReceiptSummary {
  receipt_id: string;
  course_id: string;
  provider_course_id: string;
  actor_name: string | null;
  created_at: string;
  created: Record<ClassroomIntakeCreatedKey, number>;
  skipped: number;
}

export interface ClassroomIntakeLinkableSection {
  course_id: string;
  code: string;
  title: string;
  term_name: string | null;
  status: string;
}

/** Contract v4 D6. */
export interface ClassroomIntakeCustody {
  course_id: string;
  custody: { connection_id: string; account: { name: string | null; email: string | null } };
}

/** Contract v4 D7: the Google grant is revoked unless that account still holds a teaching connection. */
export interface ClassroomIntakeRevocation {
  state: "none";
  google_grant: "revoked" | "kept_for_teaching";
}

/** Every intake error code the contract names (v2 item 5, v3). */
export type ClassroomIntakeErrorCode =
  | "preview_changed"
  | "already_linked"
  | "ambiguous_person"
  | "term_closed"
  | "decision_conflict"
  | "intake_linked"
  | "not_connected"
  | "reconnect_required"
  | "account_in_use"
  | "validation"
  | "provider_unavailable"
  | "access_denied"
  | "not_found"
  | "unavailable"
  | "forbidden"
  | "unauthenticated";

export interface ClassroomIntakeError {
  status: number;
  /** One of `ClassroomIntakeErrorCode`, or null when the body is not the intake's error shape. */
  code: string | null;
  message: string;
  /** The Classroom rows the refusal names. */
  rows: string[];
  /** `decision_conflict`: the receipt of the earlier, different confirmation (contract v4 D9). */
  receipt_id: string | null;
}

/** The intake's one error body, `{ detail: { code, message, rows } }`, read from any thrown error. */
export function classroomIntakeError(error: unknown): ClassroomIntakeError | null {
  if (!(error instanceof ApiError)) return null;
  const detail = error.detail;
  const body = typeof detail === "object" && detail !== null && !Array.isArray(detail)
    ? detail as { code?: unknown; message?: unknown; rows?: unknown; receipt_id?: unknown }
    : {};
  const code = typeof body.code === "string" && body.code ? body.code : null;
  const message = typeof body.message === "string" && body.message.trim() ? body.message.trim() : error.message.trim();
  const rows = Array.isArray(body.rows) ? body.rows.filter((row): row is string => typeof row === "string") : [];
  const receipt_id = typeof body.receipt_id === "string" && body.receipt_id ? body.receipt_id : null;
  return { status: error.status, code, message, rows, receipt_id };
}

export type ClassroomIntakeRefusal = Extract<
  ClassroomIntakeErrorCode,
  "preview_changed" | "already_linked" | "ambiguous_person" | "term_closed" | "decision_conflict"
>;

const REFUSALS: readonly ClassroomIntakeRefusal[] = [
  "preview_changed", "already_linked", "ambiguous_person", "term_closed", "decision_conflict",
];

/** The named 409 an apply or preview was refused with. */
export function classroomIntakeRefusal(error: unknown): ClassroomIntakeRefusal | null {
  const read = classroomIntakeError(error);
  if (!read || read.status !== 409) return null;
  return REFUSALS.find((refusal) => refusal === read.code) ?? null;
}

/** The grant itself is missing or lapsed: the page should read the connection again. */
export function classroomIntakeNeedsConnection(error: unknown): boolean {
  const code = classroomIntakeError(error)?.code;
  return code === "not_connected" || code === "reconnect_required";
}

const BASE = "/classroom-intake";

export const CLASSROOM_INTAKE_QUERY_KEY = ["classroom-intake"] as const;

export const classroomIntakeApi = {
  connection: () => api.get<ClassroomIntakeConnection>(`${BASE}/connection`),
  startConnection: () => api.post<{ authorization_url: string }>(`${BASE}/connection/start`),
  revokeConnection: () => api.post<ClassroomIntakeRevocation>(`${BASE}/connection/revoke`),
  courses: () => api.get<{ courses: ClassroomIntakeCourse[] }>(`${BASE}/courses`),
  preview: (providerCourseId: string, targetCourseId?: string | null) => {
    const query = targetCourseId ? `?target_course_id=${encodeURIComponent(targetCourseId)}` : "";
    return api.get<ClassroomIntakePreview>(
      `${BASE}/courses/${encodeURIComponent(providerCourseId)}/preview${query}`,
    );
  },
  linkableSections: (semesterId?: string | null) => {
    const query = semesterId ? `?semester_id=${encodeURIComponent(semesterId)}` : "";
    return api.get<{ sections: ClassroomIntakeLinkableSection[] }>(`${BASE}/linkable-sections${query}`);
  },
  apply: (body: ClassroomIntakeApplyRequest, key: string) => api.post<ClassroomIntakeApplyResult>(`${BASE}/apply`, body, commitOptions(key)),
  receipts: (courseId: string) =>
    api.get<{ receipts: ClassroomIntakeReceiptSummary[] }>(`${BASE}/receipts?course_id=${encodeURIComponent(courseId)}`),
  receipt: (receiptId: string) => api.get<ClassroomIntakeReceipt>(`${BASE}/receipts/${encodeURIComponent(receiptId)}`),
  /** Read the course's Classroom stream into IMAM under the caller's own intake connection (v4 D4, admin only). */
  refreshReferences: (courseId: string) =>
    api.post<unknown>(`/courses/${encodeURIComponent(courseId)}/classroom-references/refresh`),
  /** Take over a link's Classroom reads with the caller's own ready intake connection (contract v3). */
  takeOverCustody: (courseId: string) =>
    api.post<ClassroomIntakeCustody>(`${BASE}/links/${encodeURIComponent(courseId)}/custody`),
};
